import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  OctagonX,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'border-destructive/20 text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90',
        warning:
          'border-amber-200 bg-amber-50 text-amber-800 dark:border-yellow-600/50 dark:bg-yellow-900/30 dark:text-yellow-200 [&>svg]:text-current *:data-[slot=alert-description]:text-current',
        success:
          'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800/80 dark:bg-emerald-950/50 dark:text-emerald-100 [&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-400 *:data-[slot=alert-description]:text-muted-foreground dark:*:data-[slot=alert-description]:text-emerald-200/80',
        info: 'border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-800/80 dark:bg-sky-950/50 dark:text-sky-100 [&>svg]:text-sky-600 dark:[&>svg]:text-sky-400 *:data-[slot=alert-description]:text-sky-900/80 dark:*:data-[slot=alert-description]:text-sky-200/80',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Alert({
  className,
  variant,
  children,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot='alert'
      role='alert'
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {variant === 'warning' ? (
        <>
          <AlertTriangle aria-hidden className='shrink-0' />
          {children}
        </>
      ) : variant === 'success' ? (
        <>
          <CheckCircle2 aria-hidden className='shrink-0' />
          {children}
        </>
      ) : variant === 'destructive' ? (
        <>
          <OctagonX aria-hidden className='shrink-0' />
          {children}
        </>
      ) : variant === 'info' ? (
        <>
          <Info aria-hidden className='shrink-0' />
          {children}
        </>
      ) : (
        children
      )}
    </div>
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-title'
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-description'
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-action'
      className={cn(
        'col-start-2 flex flex-wrap items-center justify-end gap-2 pt-2',
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertAction, AlertDescription, AlertTitle }
